import { ModalLayout } from '@/components/HeadlessUI'
import { ConfirmationModal, PopupBody, PopupFooter, PopupHeader, PopupWrapper } from '@/components'
import { MODAL_PANEL_CLASS_MD, MODAL_PLACEMENT_CENTER } from '@/constants'
import { GetTenantSubscriptionResponse, SearchTenantRequest, ViewTenantResponse } from '@/helpers/api/WebApiClient'
import { messageHelper } from '@/helpers/message.helper'
import { licenseAdminApi } from '@/services/licenseAdminApi'
import { LicenseStatusTypes, type AdminTenantLicenseEligibilityResponse, type TenantLicenseDetailResponse, type TenantLicenseListResponse } from '@/services/licenseTypes'
import { multiTenantService } from '@/services/MultiTenantService'
import { subscriptionPlanAdminService } from '@/services/SubscriptionPlanAdminService'
import type { ViewSubscriptionPlanListResponse } from '@/services/subscriptionPlanTypes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import LicenseIssuedPanel from './LicenseIssuedPanel'

type IssueMode = 'existing' | 'new'

interface IssueLicenseModalProps {
	isOpen: boolean
	onClose: () => void
	onComplete: () => void
	pendingRows?: TenantLicenseListResponse[]
	initialTenantName?: string
	initialTenantId?: number
}

const isPlatformRootTenant = (t: ViewTenantResponse) =>
	t.name?.trim().toLowerCase() === 'root' || t.adminEmail?.toLowerCase() === 'superadmin@root.com'

const IssueLicenseModal = ({ isOpen, onClose, onComplete, pendingRows = [], initialTenantName, initialTenantId }: IssueLicenseModalProps) => {
	const [tenants, setTenants] = useState<ViewTenantResponse[]>([])
	const [tenantSearch, setTenantSearch] = useState('')
	const [tenantsLoading, setTenantsLoading] = useState(false)
	const [selectedTenantId, setSelectedTenantId] = useState<number | ''>('')
	const [mode, setMode] = useState<IssueMode>('existing')
	const [subscriptions, setSubscriptions] = useState<GetTenantSubscriptionResponse[]>([])
	const [subscriptionsLoading, setSubscriptionsLoading] = useState(false)
	const [plans, setPlans] = useState<ViewSubscriptionPlanListResponse[]>([])
	const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | ''>('')
	const [selectedPlanId, setSelectedPlanId] = useState<number | ''>('')
	const [isTrial, setIsTrial] = useState(false)
	const [requestPayment, setRequestPayment] = useState(false)
	const [notes, setNotes] = useState('')
	const [loading, setLoading] = useState(false)
	const [issued, setIssued] = useState<TenantLicenseDetailResponse | null>(null)
	const [paymentRequestMessage, setPaymentRequestMessage] = useState<string | null>(null)
	const [eligibility, setEligibility] = useState<AdminTenantLicenseEligibilityResponse | null>(null)
	const [eligibilityLoading, setEligibilityLoading] = useState(false)
	const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(false)

	const eligibleTenants = useMemo(() => tenants.filter((t) => t.id !== 1 && !isPlatformRootTenant(t)), [tenants])
	const selectedTenant = eligibleTenants.find((t) => t.id === selectedTenantId)

	const tenantPending = useMemo(() => {
		if (!selectedTenant) return []
		return pendingRows.filter(
			(r) =>
				r.tenantName === selectedTenant.name &&
				(r.licenseStatus === LicenseStatusTypes.PendingIssue || !r.hasLicenseKey)
		)
	}, [pendingRows, selectedTenant])

	const step2Active = Boolean(selectedTenantId)
	const canSubmit =
		!loading &&
		selectedTenantId &&
		(mode === 'existing'
			? Boolean(selectedSubscriptionId || tenantPending.length > 0)
			: Boolean(selectedPlanId))

	const loadTenants = useCallback(async () => {
		setTenantsLoading(true)
		try {
			const searchModel = new SearchTenantRequest()
			searchModel.pageNumber = 1
			searchModel.pageSize = 100
			if (tenantSearch.trim()) searchModel.freeText = tenantSearch.trim()
			const res = await multiTenantService.getTenant(searchModel)
			setTenants(res.data ?? [])
		} finally {
			setTenantsLoading(false)
		}
	}, [tenantSearch])

	const loadPlans = useCallback(async () => {
		const res = await subscriptionPlanAdminService.search({ pageNumber: 1, pageSize: 100 })
		setPlans(res.data ?? [])
	}, [])

	useEffect(() => {
		if (!isOpen) return
		loadPlans()
		if (initialTenantName) setTenantSearch(initialTenantName)
		if (initialTenantId) setSelectedTenantId(initialTenantId)
	}, [isOpen, loadPlans, initialTenantName, initialTenantId])

	useEffect(() => {
		if (!isOpen) return
		const timer = window.setTimeout(() => loadTenants(), 280)
		return () => window.clearTimeout(timer)
	}, [isOpen, tenantSearch, loadTenants])

	useEffect(() => {
		if (!isOpen) {
			setSelectedTenantId('')
			setMode('existing')
			setSelectedSubscriptionId('')
			setSelectedPlanId('')
			setIsTrial(false)
			setRequestPayment(false)
			setNotes('')
			setIssued(null)
			setPaymentRequestMessage(null)
			setEligibility(null)
			setShowUpgradeConfirmation(false)
			setSubscriptions([])
			setTenantSearch('')
		}
	}, [isOpen])

	useEffect(() => {
		if (!selectedTenantId) {
			setSubscriptions([])
			setSelectedSubscriptionId('')
			setEligibility(null)
			return
		}
		setSubscriptionsLoading(true)
		multiTenantService
			.getTenantSubscription(Number(selectedTenantId))
			.then((subs) => {
				setSubscriptions(subs)
				if (subs.length === 1) setSelectedSubscriptionId(subs[0].tenantSubscriptionId)
			})
			.catch(() => setSubscriptions([]))
			.finally(() => setSubscriptionsLoading(false))
	}, [selectedTenantId])

	useEffect(() => {
		if (!selectedTenantId || mode !== 'new') {
			setEligibility(null)
			setEligibilityLoading(false)
			return
		}
		setEligibilityLoading(true)
		licenseAdminApi
			.getTenantEligibility(Number(selectedTenantId))
			.then(setEligibility)
			.catch(() => setEligibility(null))
			.finally(() => setEligibilityLoading(false))
	}, [selectedTenantId, mode])

	useEffect(() => {
		if (tenantPending.length === 1 && !selectedSubscriptionId) {
			setSelectedSubscriptionId(tenantPending[0].tenantSubscriptionPlanId)
		}
	}, [tenantPending, selectedSubscriptionId])

	const selectedPlan = plans.find((p) => p.id === selectedPlanId)
	const selectedPlanRequiresTrial = mode !== 'existing' && selectedPlan?.trialEnabled === true
	const isTrialIssue = selectedPlanRequiresTrial || isTrial

	useEffect(() => {
		if (selectedPlanRequiresTrial) {
			setIsTrial(true)
		}
	}, [selectedPlanRequiresTrial])

	useEffect(() => {
		if (isTrialIssue) {
			setRequestPayment(false)
		}
	}, [isTrialIssue])

	const handleClose = () => {
		setShowUpgradeConfirmation(false)
		if (issued || paymentRequestMessage) onComplete()
		onClose()
	}

	const submitExisting = async () => {
		const subId = selectedSubscriptionId || tenantPending[0]?.tenantSubscriptionPlanId
		if (!subId) {
			messageHelper.showError('Select a subscription or use a quick-generate card above.')
			return
		}
		setLoading(true)
		try {
			const detail = await licenseAdminApi.generate(Number(subId))
			setIssued(detail)
		} catch (e) {
			messageHelper.showError(String(e))
		} finally {
			setLoading(false)
		}
	}

	const submitNew = async () => {
		if (!selectedTenantId || !selectedPlanId) {
			messageHelper.showError('Select a customer and subscription plan.')
			return
		}
		setLoading(true)
		try {
			const detail = await licenseAdminApi.issue({
				tenantId: Number(selectedTenantId),
				subscriptionPlanId: Number(selectedPlanId),
				isTrial: isTrialIssue,
				requestPayment: isTrialIssue ? false : requestPayment,
				notes: notes.trim() || undefined,
			})
			if (detail.requiresPayment) {
				setPaymentRequestMessage(detail.message)
				messageHelper.showSuccess(detail.message)
			} else if (detail.licenseDetail) {
				setIssued(detail.licenseDetail)
			}
		} catch (e) {
			messageHelper.showError(String(e))
		} finally {
			setLoading(false)
		}
	}

	const onSubmit = () => {
		if (mode === 'existing') submitExisting()
		else setShowUpgradeConfirmation(true)
	}

	const generatePending = (row: TenantLicenseListResponse) => {
		setLoading(true)
		licenseAdminApi
			.generate(row.tenantSubscriptionPlanId)
			.then(setIssued)
			.catch((e) => messageHelper.showError(String(e)))
			.finally(() => setLoading(false))
	}

	const selectedSub = subscriptions.find((s) => s.tenantSubscriptionId === selectedSubscriptionId)
	const eligibilityActionLabel = eligibility?.canRenew ? 'Renew' : eligibility?.canUpgrade ? 'Upgrade' : 'New purchase'
	const showEligibilityFlag = !!eligibility?.currentTenantSubscriptionPlanId
	const showEligibilityExpiry = showEligibilityFlag && eligibilityActionLabel === 'Upgrade'
	const isRenewConfirmation = showEligibilityFlag && eligibilityActionLabel === 'Renew'
	const isUpgradeConfirmation = showEligibilityFlag && eligibilityActionLabel === 'Upgrade'
	const currentPlanExpiryText = eligibility?.currentExpiresOn
		? new Date(eligibility.currentExpiresOn).toLocaleDateString()
		: 'the current expiry date'
	const confirmationTitle = isRenewConfirmation
		? 'Confirm plan renewal'
		: isUpgradeConfirmation
			? 'Confirm plan upgrade'
			: 'Confirm plan issue'
	const confirmationDescription = isRenewConfirmation
		? `${eligibility?.currentPlanName ?? 'Existing plan'} expires on ${currentPlanExpiryText}. ${selectedPlan?.name ?? 'The selected plan'} will renew it and expire after subscription duration. Continue?`
		: isUpgradeConfirmation
			? `${eligibility?.currentPlanName ?? 'Existing plan'} expires on ${currentPlanExpiryText}. ${selectedPlan?.name ?? 'The selected plan'} will upgrade it and expire on the same date. Continue?`
			: `${selectedPlan?.name ?? 'The selected plan'} will be issued for this customer. Continue?`
	const confirmationButtonText = isRenewConfirmation
		? 'Confirm renewal'
		: isUpgradeConfirmation
			? 'Confirm upgrade'
			: 'Confirm issue'

	return (
		<>
		<ModalLayout showModal={isOpen} toggleModal={handleClose} panelClassName={MODAL_PANEL_CLASS_MD} placement={MODAL_PLACEMENT_CENTER}>
			<PopupWrapper variant="form-tabs" className="issue-license-modal">
				<PopupHeader title={issued ? 'License issued' : 'Issue license'} onClose={handleClose} />
				<PopupBody>
					{issued ? (
						<LicenseIssuedPanel detail={issued} onClose={handleClose} />
					) : paymentRequestMessage ? (
						<div className="issue-license-modal__pending">
							<p className="issue-license-modal__pending-title">
								<i className="ri-bank-card-line" aria-hidden />
								Payment requested
							</p>
							<div className="issue-license-modal__pending-item">
								<div>
									<p className="text-sm font-semibold text-[hsl(var(--foreground))]">Custom plan request approved</p>
									<p className="text-xs text-[hsl(var(--muted-foreground))]">
										{paymentRequestMessage} Customer can pay from Custom Plan Requests.
									</p>
								</div>
							</div>
						</div>
					) : (
						<>
							<p className="issue-license-modal__intro">
								Choose a customer tenant, then generate a license for an existing subscription or assign a new plan.
								Platform root tenants cannot receive licenses.
							</p>

							<div className="issue-license-modal__steps" aria-hidden>
								<div className={`issue-license-modal__step ${selectedTenantId ? 'issue-license-modal__step--done' : 'issue-license-modal__step--active'}`}>
									<span className="issue-license-modal__step-num">1</span>
									Customer
								</div>
								<div className={`issue-license-modal__step ${step2Active ? 'issue-license-modal__step--active' : ''}`}>
									<span className="issue-license-modal__step-num">2</span>
									License
								</div>
							</div>

							<section className="issue-license-modal__section">
								<div className="issue-license-modal__section-title">
									<i className="ri-building-line" aria-hidden />
									Select customer
									<span className="text-danger">*</span>
								</div>
								<div className="issue-license-modal__search-wrap">
									<i className="ri-search-line" aria-hidden />
									<input
										type="search"
										className="issue-license-modal__search"
										placeholder="Search by name or admin email…"
										value={tenantSearch}
										onChange={(e) => setTenantSearch(e.target.value)}
										autoComplete="off"
									/>
								</div>
								{tenantsLoading ? (
									<p className="issue-license-modal__empty-hint">
										<i className="ri-loader-4-line animate-spin me-1" aria-hidden />
										Loading tenants…
									</p>
								) : eligibleTenants.length === 0 ? (
									<p className="issue-license-modal__empty-hint">No customer tenants match your search.</p>
								) : (
									<div className="issue-license-modal__tenant-list" role="listbox" aria-label="Customer tenants">
										{eligibleTenants.map((t) => (
											<button
												key={t.id}
												type="button"
												role="option"
												aria-selected={selectedTenantId === t.id}
												className={`issue-license-modal__tenant-option ${selectedTenantId === t.id ? 'issue-license-modal__tenant-option--selected' : ''}`}
												onClick={() => setSelectedTenantId(t.id)}
											>
												<span className="issue-license-modal__tenant-name">{t.name}</span>
												<span className="issue-license-modal__tenant-email">{t.adminEmail}</span>
											</button>
										))}
									</div>
								)}
							</section>

							{selectedTenantId ? (
								<section className="issue-license-modal__section mt-4">
									<div className="issue-license-modal__section-title">
										<i className="ri-key-2-line" aria-hidden />
										License action
									</div>

									<div className="issue-license-modal__mode-toggle" role="radiogroup" aria-label="License action type">
										<button
											type="button"
											className={`issue-license-modal__mode-btn ${mode === 'existing' ? 'issue-license-modal__mode-btn--active' : ''}`}
											onClick={() => setMode('existing')}
										>
											<span className="issue-license-modal__mode-title">Existing subscription</span>
											<span className="issue-license-modal__mode-desc">Activate or generate for a plan already on this tenant</span>
										</button>
										<button
											type="button"
											className={`issue-license-modal__mode-btn ${mode === 'new' ? 'issue-license-modal__mode-btn--active' : ''}`}
											onClick={() => setMode('new')}
										>
											<span className="issue-license-modal__mode-title">New plan</span>
											<span className="issue-license-modal__mode-desc">Assign a catalog plan and issue in one step</span>
										</button>
									</div>

									{mode === 'existing' ? (
										<>
											{tenantPending.length > 0 && (
												<div className="issue-license-modal__pending">
													<p className="issue-license-modal__pending-title">
														<i className="ri-time-line" aria-hidden />
														Ready to generate ({tenantPending.length})
													</p>
													{tenantPending.map((row) => (
														<div key={row.id} className="issue-license-modal__pending-item">
															<div>
																<p className="text-sm font-semibold text-[hsl(var(--foreground))]">{row.planName}</p>
																<p className="text-xs text-[hsl(var(--muted-foreground))]">{row.adminEmail}</p>
															</div>
															<button
																type="button"
																className="btn btn-sm btn-ember shrink-0"
																disabled={loading}
																onClick={() => generatePending(row)}
															>
																Generate now
															</button>
														</div>
													))}
												</div>
											)}
											<div className="issue-license-modal__field">
												<label className="issue-license-modal__label" htmlFor="issue-sub-select">
													Subscription
													<span className="text-danger">*</span>
												</label>
												<select
													id="issue-sub-select"
													className="issue-license-modal__select"
													disabled={subscriptionsLoading}
													value={selectedSubscriptionId === '' ? '' : String(selectedSubscriptionId)}
													onChange={(e) => setSelectedSubscriptionId(e.target.value ? Number(e.target.value) : '')}
												>
													<option value="">
														{subscriptionsLoading ? 'Loading…' : 'Choose subscription…'}
													</option>
													{subscriptions.map((s) => (
														<option key={s.tenantSubscriptionId} value={s.tenantSubscriptionId}>
															{s.tenantSubscriptionName} — expires{' '}
															{new Date(s.expiredOn as unknown as string).toLocaleDateString()}
														</option>
													))}
												</select>
												{subscriptions.length === 0 && !subscriptionsLoading && tenantPending.length === 0 && (
													<p className="issue-license-modal__hint">
														No subscriptions on file. Switch to <strong>New plan</strong> to assign one.
													</p>
												)}
											</div>
										</>
									) : (
										<>
											{eligibilityLoading ? (
												<div className="issue-license-modal__eligibility-warning">
													<p>
														<i className="ri-loader-4-line animate-spin" aria-hidden />
														Checking eligibility
													</p>
												</div>
											) : showEligibilityFlag ? (
												<div className="issue-license-modal__eligibility-warning">
													<p>{eligibilityActionLabel}</p>
													{eligibility.currentPlanName && (
														<span>
															{eligibility.currentPlanName}
															{eligibility.currentExpiresOn ? ` · expires ${new Date(eligibility.currentExpiresOn).toLocaleDateString()}` : ''}
														</span>
													)}
												</div>
											) : null}
											<div className="issue-license-modal__field">
												<label className="issue-license-modal__label" htmlFor="issue-plan-select">
													Subscription plan
													<span className="text-danger">*</span>
												</label>
												<select
													id="issue-plan-select"
													className="issue-license-modal__select"
													value={selectedPlanId === '' ? '' : String(selectedPlanId)}
													onChange={(e) => setSelectedPlanId(e.target.value ? Number(e.target.value) : '')}
												>
													<option value="">Choose plan from catalog…</option>
													{plans.map((p) => (
														<option key={p.id} value={p.id}>
															{p.name} — ${p.price}
														</option>
													))}
												</select>
											</div>
											<div className="issue-license-modal__trial-card">
												<span className="issue-license-modal__trial-copy">Issue as trial license</span>
												<button
													type="button"
													role="switch"
													aria-checked={isTrial}
													aria-disabled={selectedPlanRequiresTrial}
													className={`issue-license-modal__switch ${isTrial ? 'issue-license-modal__switch--on' : ''}`}
													disabled={selectedPlanRequiresTrial}
													onClick={() => setIsTrial((v) => !v)}
												/>
											</div>
											<div className="issue-license-modal__trial-card">
												<span className="issue-license-modal__trial-copy">Request Payment</span>
												<button
													type="button"
													role="switch"
													aria-checked={requestPayment}
													aria-disabled={isTrialIssue}
													className={`issue-license-modal__switch ${requestPayment ? 'issue-license-modal__switch--on' : ''}`}
													disabled={isTrialIssue}
													onClick={() => setRequestPayment((v) => !v)}
												/>
											</div>
											<div className="issue-license-modal__field">
												<label className="issue-license-modal__label" htmlFor="issue-notes">
													Internal notes <span className="font-normal text-muted-foreground">(optional)</span>
												</label>
												<textarea
													id="issue-notes"
													className="issue-license-modal__textarea"
													rows={2}
													placeholder="e.g. comp license for partner onboarding"
													value={notes}
													onChange={(e) => setNotes(e.target.value)}
												/>
											</div>
										</>
									)}

									<div className="issue-license-modal__summary">
										<span className="issue-license-modal__summary-chip">
											<i className="ri-building-2-line" aria-hidden />
											{selectedTenant?.name}
										</span>
										<span className="issue-license-modal__summary-chip">
											{mode === 'existing'
												? selectedSub?.tenantSubscriptionName ?? (tenantPending[0]?.planName || 'Subscription')
												: selectedPlan?.name ?? 'Plan'}
										</span>
										{mode === 'new' && isTrialIssue && (
											<span className="issue-license-modal__summary-chip">
												<i className="ri-gift-line" aria-hidden />
												Trial
											</span>
										)}
										{mode === 'new' && requestPayment && (
											<span className="issue-license-modal__summary-chip">
												<i className="ri-bank-card-line" aria-hidden />
												Payment required
											</span>
										)}
									</div>
								</section>
							) : null}
						</>
					)}
				</PopupBody>
				{!issued && !paymentRequestMessage && (
					<PopupFooter className="flex-wrap gap-3 sm:flex-nowrap">
						<p className="issue-license-modal__footer-hint hidden sm:block">
							{!selectedTenantId
								? 'Select a customer to continue'
								: canSubmit
									? 'Review summary, then confirm'
									: 'Complete the required fields'}
						</p>
						<div className="flex w-full shrink-0 gap-2 sm:w-auto sm:ms-auto">
							<button type="button" className="btn btn-secondary flex-1 sm:flex-none" onClick={handleClose} disabled={loading}>
								Cancel
							</button>
							<button
								type="button"
								className="btn btn-ember flex-1 sm:flex-none"
								disabled={!canSubmit}
								onClick={onSubmit}
							>
								{loading ? (
									<>
										<i className="ri-loader-4-line animate-spin" aria-hidden />
										Working…
									</>
								) : (
									<>
										<i className="ri-key-2-line" aria-hidden />
										{mode === 'existing' ? 'Generate license' : requestPayment ? 'Request payment' : 'Issue license'}
									</>
								)}
							</button>
						</div>
					</PopupFooter>
				)}
			</PopupWrapper>
		</ModalLayout>
		<ConfirmationModal
			isOpen={showUpgradeConfirmation}
			onClose={() => setShowUpgradeConfirmation(false)}
			onConfirm={submitNew}
			title={confirmationTitle}
			description={confirmationDescription}
			confirmButtonText={confirmationButtonText}
			variant="warning"
		/>
		</>
	)
}

export default IssueLicenseModal
